// Shared Salesforce utilities for both frontend and backend

// Cache for Salesforce config to avoid repeated API calls
let salesforceConfigCache: { baseUrl: string } | null = null;

/**
 * Initialize Salesforce config by fetching base URL from API
 * Call this once when the app/component loads
 */
export async function initializeSalesforceConfig(): Promise<void> {
  if (salesforceConfigCache) {
    return; // Already initialized
  }

  try {
    const response = await fetch('/api/config/salesforce');
    const result = await response.json();
    
    if (result.success && result.data?.baseUrl) {
      salesforceConfigCache = { baseUrl: result.data.baseUrl };
    } else {
      console.error('Failed to get Salesforce config from API:', result);
      // Fallback to default URL if API fails
      salesforceConfigCache = { baseUrl: 'https://agility-drive-635.lightning.force.com' };
    }
  } catch (error) {
    console.error('Error fetching Salesforce config:', error);
    // Fallback to default URL if API call fails
    salesforceConfigCache = { baseUrl: 'https://agility-drive-635.lightning.force.com' };
  }
}

/**
 * Get Salesforce base URL (must call initializeSalesforceConfig first)
 */
export function getSalesforceBaseUrl(): string {
  if (!salesforceConfigCache) {
    console.warn('Salesforce config not initialized. Call initializeSalesforceConfig() first.');
    return 'https://agility-drive-635.lightning.force.com'; // Fallback
  }
  return salesforceConfigCache.baseUrl;
}

/**
 * Generate Salesforce record URL
 * @param sfid - Salesforce record ID
 * @param objectType - Salesforce object type (default: 'Contact')
 * @returns string - Full Salesforce URL
 */
export function getSalesforceUrl(sfid: string, objectType: string = 'Contact'): string {
  const baseUrl = getSalesforceBaseUrl();
  return `${baseUrl}/lightning/r/${objectType}/${sfid}/view`;
}

/**
 * Check if Salesforce config is initialized
 */
export function isSalesforceConfigInitialized(): boolean {
  return salesforceConfigCache !== null;
}

/**
 * Clear the cache (useful for testing or when config changes)
 */
export function clearSalesforceConfigCache(): void {
  salesforceConfigCache = null;
}